import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, useTheme, Typography, Grid, Paper, CircularProgress } from '@mui/material';
import SearchOffIcon from '@mui/icons-material/SearchOff';

// 导入组件
import TaskList from '../components/Tasks/TaskList';
import TaskFilter from '../components/Tasks/TaskFilter';
import TaskPagination from '../components/Tasks/TaskPagination';
import LoginModal from '../components/Common/LoginModal';

// 导入操作
import { fetchTasks, startTaskPolling, stopTaskPolling } from '../store/actions/tasksActions';
import { fetchLanguages, fetchStatuses } from '../store/actions/uiActions';

const Workspace = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const { tasks, loading, total, page, limit } = useSelector((state) => state.tasks);
  const { isAuthenticated, authChecked } = useSelector((state) => state.auth);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    language: 'all',
    status: 'all',
  });

  // 判断是否已筛选
  const [hasFiltered, setHasFiltered] = useState(false);

  // 初始加载任务列表和开始任务轮询
  useEffect(() => {
    // 只有在认证状态检查完成后才执行
    if (authChecked) {
      if (isAuthenticated) {
        // 加载任务数据
        updateTaskList();

        // 加载语种和状态数据
        dispatch(fetchLanguages());
        dispatch(fetchStatuses());

        // 开始轮询进行中的任务
        dispatch(startTaskPolling([], 30000)); // 每30秒轮询一次

        // 组件卸载时停止轮询
        return () => {
          dispatch(stopTaskPolling());
        };
      }
    }
  }, [authChecked, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // 当任务列表发生变化时，重新评估需要轮询的任务
  useEffect(() => {
    if (isAuthenticated && tasks && tasks.length > 0) {
      // 找出所有进行中的任务
      const inProgressTaskIds = tasks
        .filter(task => task.status === 'processing' || task.status === 'waiting')
        .map(task => task.task_id);

      // 如果有进行中的任务，更新轮询
      if (inProgressTaskIds.length > 0) {
        dispatch(startTaskPolling(inProgressTaskIds, 30000)); // 进行中任务每30秒轮询
      }
    }
  }, [tasks, dispatch, isAuthenticated]);

  // 获取任务列表数据
  const updateTaskList = (page = 1, newFilters = filters) => {
    // 检查是否应用了筛选条件
    const isFiltered =
      newFilters.startDate !== '' ||
      newFilters.endDate !== '' ||
      newFilters.language !== 'all' ||
      newFilters.status !== 'all';

    setHasFiltered(isFiltered);

    dispatch(fetchTasks({
      page,
      limit,
      language: newFilters.language !== 'all' ? newFilters.language : undefined,
      status: newFilters.status !== 'all' ? newFilters.status : undefined,
      startDate: newFilters.startDate || undefined,
      endDate: newFilters.endDate || undefined
    }));
  };

  // 处理筛选变化
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    if (isAuthenticated) {
      updateTaskList(1, newFilters);
    }
  };

  // 处理页码变化
  const handlePageChange = (newPage) => {
    if (isAuthenticated) {
      updateTaskList(newPage, filters);
    }
  };

  // 处理登录成功
  const handleLoginSuccess = (userData) => {
    setShowLoginModal(false);
    // 登录成功后加载任务列表
    updateTaskList();
  };

  // 清除筛选条件
  const clearFilters = () => {
    const resetFilters = {
      startDate: '',
      endDate: '',
      language: 'all',
      status: 'all',
    };
    setFilters(resetFilters);
    if (isAuthenticated) {
      updateTaskList(1, resetFilters);
    }
  };

  // 判断是否有任务数据
  const hasTaskData = tasks && tasks.length > 0;

  // 判断是否是初始状态（没有筛选且没有数据）
  const isInitialEmpty = !hasTaskData && !hasFiltered;

  // 判断是否是筛选结果为空
  const isFilteredEmpty = !hasTaskData && hasFiltered;

  // 状态1: 初始loading状态 - 认证状态未检查完成或任务正在加载时显示
  if (!authChecked || (isAuthenticated && loading && !hasTaskData)) {
    return (
      <Box sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ color: theme.palette.primary.main, mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            正在加载...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ height: '100%' }}>
        {/* 状态2: 空白提示区域 - 已登录但无任务时显示简单提示 */}
        {(isAuthenticated && isInitialEmpty) ? (
          <Box sx={{
            height: 'calc(100vh - 200px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Typography variant="h6" color="text.secondary">
              当前还没有任务翻译记录
            </Typography>
          </Box>
        ) : (
          /* 状态3: 任务列表区域 - 已登录且有任务 */
          <>
            {/* 标题和信息 */}
            <Grid container spacing={3} sx={{ mb: 0 }}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography
                    variant="h4"
                    component="h1"
                    sx={{
                      fontWeight: 600,
                      background: 'linear-gradient(90deg, #4353FF 0%, #4353FF 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      marginLeft: '10px'
                    }}
                  >
                    证件翻译记录
                  </Typography>

                  <Box
                    sx={{
                      px: 2,
                      py: 0.75,
                      borderRadius: 8,
                      bgcolor: theme.palette.background.light,
                      display: 'flex',
                      alignItems: 'center',
                      mr: 2
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      共 {total} 个任务
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {/* 筛选器 */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <TaskFilter
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onClearFilters={clearFilters}
                />
              </Grid>
            </Grid>

            {/* 任务列表或空状态 */}
            {isFilteredEmpty ? (
              <Paper
                elevation={0}
                sx={{
                  p: 6,
                  textAlign: 'center',
                  backgroundColor: 'transparent',
                  border: '2px dashed',
                  borderColor: theme.palette.divider,
                  borderRadius: 3
                }}
              >
                <SearchOffIcon sx={{ fontSize: 64, color: theme.palette.text.disabled, mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  没有找到符合条件的翻译记录
                </Typography>
                <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
                  请尝试调整筛选条件或清除所有筛选
                </Typography>
              </Paper>
            ) : (
              <>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TaskList tasks={tasks} loading={loading} />
                  </Grid>
                </Grid>

                {/* 分页 */}
                {total > limit && (
                  <Grid container spacing={3} sx={{ mt: 2 }}>
                    <Grid item xs={12}>
                      <TaskPagination
                        page={page}
                        total={total}
                        limit={limit}
                        onPageChange={handlePageChange}
                      />
                    </Grid>
                  </Grid>
                )}
              </>
            )}
          </>
        )}
      </Box>

      {/* 登录弹窗 */}
      <LoginModal
        open={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLoginSuccess={handleLoginSuccess}
      />
    </>
  );
};

export default Workspace;